import { supabase } from './supabaseClient';
import { RecipeSource } from '@/types/recipe';

export interface FeaturedRecipe {
  id: number;
  title: string;
  description: string | null;
  category: string | null;
  image_url: string;
  tags: string[] | null;
}

const DEFAULT_IMAGE_URL =
  'https://bzvzoqvyxfrgaxsokfva.supabase.co/storage/v1/object/public/recipeimage/chezalex.jpg';

const FEATURED_LIMIT = 5;

/**
 * Defensive JSON parser for the recipes.tags text column.
 * Returns null if the value can't be turned into a string[].
 */
function parseTagsSafe(raw: unknown): string[] | null {
  if (raw == null) return null;
  if (Array.isArray(raw)) return raw.filter((t): t is string => typeof t === 'string');
  if (typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((t): t is string => typeof t === 'string');
    }
  } catch {
    // fall through
  }
  return null;
}

/**
 * Fetch a random selection of public, Studio-created recipes with custom images
 * to display on the marketing/login page. Safe for unauthenticated callers:
 * the query relies on PostgREST + RLS so anon users only see share=true rows.
 *
 * The random sample is produced by asking for a window of rows ordered by a
 * hash of id (PostgREST honors `order=id` in lieu of RANDOM()) and then
 * shuffling client-side to avoid bias from non-uniform id gaps.
 */
export async function fetchFeaturedStudioRecipes(
  limit: number = FEATURED_LIMIT
): Promise<FeaturedRecipe[]> {
  try {
    // Pull a slightly larger window so client-side shuffle can pick a fresh
    // set of 5 on every page load. We cap at min(40, available) to avoid
    // pulling the whole table when there are only a few recipes.
    const windowSize = Math.min(40, limit * 6);

    const { data, error } = await supabase
      .from('recipes')
      .select('id, title, description, category, image_url, tags')
      .eq('source', RecipeSource.Studio)
      .eq('share', true)
      .eq('has_custom_image', true)
      .not('image_url', 'is', null)
      .order('dateCreated', { ascending: false })
      .limit(windowSize);

    if (error) {
      return [];
    }

    if (!data || data.length === 0) return [];

    // Fisher–Yates shuffle, then take `limit`.
    const shuffled = [...data];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }

    return shuffled.slice(0, limit).map((row) => ({
      id: row.id as number,
      title: (row.title as string) ?? 'Untitled recipe',
      description: (row.description as string | null) ?? null,
      category: (row.category as string | null) ?? null,
      // Defensive fallback in case a custom image_url got dropped.
      image_url: (row.image_url as string) || DEFAULT_IMAGE_URL,
      tags: parseTagsSafe(row.tags),
    }));
  } catch {
    return [];
  }
}
